import { PagingApiModel, SortOrder } from '@/types/api';
import { UserModel } from '@/types/users';
import { SupporterModel } from '@/types/supporters';

export enum SubscriptionStatus {
  Pending = 0,
  Active = 1,
  Suspended = 2,
  Expired = 3
}

export enum Status {
  Deactivated = 0,
  Requested = 1,
  Approved = 2,
  OBOG = 3
}

export enum Role {
  Member = 1,
  Coach = 2
}

// 基本型を定義
type TeamMemberSortField = 'category' | 'accountname' | 'createdat' | 'updatedat';
type TeamNotesSortField = 'category' | 'date';
type TeamMembersGoalsSortField = 'category' | 'updated_at' | 'created_at';

// ソート設定インターフェースをジェネリックで定義
interface SortConfig<T extends string> {
  field: T;
  order: SortOrder;
}

// 各コンテキスト用のソート設定
export type TeamMemberSortConfig = SortConfig<TeamMemberSortField | string>;
export type TeamNotesSortConfig = SortConfig<TeamNotesSortField | string>;
export type TeamMembersGoalsSortConfig = SortConfig<TeamMembersGoalsSortField | string>;

// 所属メンバー一覧のデフォルトソート
// NOTE: デフォルトソートの指定（オーナーを一番上、OBOGを最下部にソート）
export const teamMemberDefaultSort: TeamMemberSortConfig[] = [
  { field: 'role', order: 'desc' },
  { field: 'status', order: 'asc' },
  { field: 'category', order: 'asc' }
];

export interface TeamApiModel {
  team: TeamModel;
}

export interface TeamDetailApiModel {
  team: TeamDetailModel;
}

export interface CustomerPortalApiModel {
  customer_portal_url: string;
}

export interface TeamModel {
  uuid: string;
  owner_id: number;
  name: string;
  description: string | null;
  color_a: string;
  color_b: string;
  deleted: boolean;
  url_home: string | null;
  url_x: string | null;
  url_facebook: string | null;
  url_instagram: string | null;
  team_emblem: { file_name: string; file_path: string } | null;
}

export interface TeamDetailModel extends TeamModel {
  team_subscription: TeamSubscriptionModel;
  active_members: number;
  obog_members: number;
  total_team_logs: number;
}

export interface TeamCreateDataModel {
  name: string;
  owner_id: number;
  color_a: string;
  color_b: string;
}

export interface TeamUpdateDataModel {
  name: string;
  owner_id: number;
  description?: string | null;
  color_a: string;
  color_b: string;
  url_home?: string | null;
  url_x?: string | null;
  url_facebook?: string | null;
  url_instagram?: string | null;
  team_emblem_file?: File | 'removed' | null;
}

export interface TeamSubscriptionModel {
  status: SubscriptionStatus;
}

export interface TeamMemberApiModel {
  team_member: TeamMemberModel;
}

export interface TeamMemberLightApiModel {
  team_member: TeamMemberLightModel;
}

export interface TeamMembersApiModel {
  has_more?: boolean;
  team_members: TeamMemberModel[];
}

export interface TeamMembersPagingApiModel extends TeamMembersApiModel, PagingApiModel {}

interface TeamMemberBaseModel {
  id: number;
  role: Role;
  status: Status;
  number_of_team_logs: number;
}

export interface TeamMemberModel extends TeamMemberBaseModel {
  user: UserModel;
  team: TeamModel;
  team_category?: TeamCategoryModel;
}

export interface TeamMemberLightModel extends TeamMemberBaseModel {
  user_id: number;
  team_uuid: string;
  team_category_id: number;
}

export interface TeamMemberGlobalContextModel {
  team: TeamModel;
  status: Status;
}

export interface SupportersWithTeamMemberApiModel {
  has_more?: boolean;
  supporters: SupporterModel[];
  team_members: TeamMemberLightModel[];
}

export interface TeamMemberCategoryUpdateDataModel {
  team_member_id: number;
  team_category_id?: number | undefined;
}

export interface TeamMemberRoleUpdateDataModel {
  team_member_id: number;
  role: number;
}

export interface TeamMemberStatusUpdateDataModel {
  team_member_id: number;
  status: number;
}

export interface TeamCategoryModel {
  id: number;
  name: string;
  color: string;
  team_uuid: string;
}

export interface TeamCategoryApiModel {
  team_category: TeamCategoryModel;
}

export interface TeamCategoriesApiModel {
  team_categories: TeamCategoryModel[];
}

export interface TeamCategoryUpdateDataModel {
  id: number;
  name: string;
  color: string;
}
